'use client';

import {
  AddFavoriteMutation,
  AddVariantToComparisonMutation,
  AddViewedVariantMutation,
  ProductVariantAvailabilityEnum,
  RemoveFavoriteMutation,
  RemoveVariantFromComparisonMutation,
  StickerFragment,
  useAddFavoriteMutation,
  useAddVariantToComparisonMutation,
  useAddViewedVariantMutation,
  useDefaultChannelQuery,
  useDraftOrderQuery,
  useProductVariantsQuery,
  useRemoveFavoriteMutation,
  useRemoveVariantFromComparisonMutation,
  useUserDraftOrdersQuery,
  useUserFavoritesQuery,
} from '@/graphql';
import { useLanguageCode } from '@/hooks/useLanguageCode';
import { useCityStore } from '@/zustand/cityStore';
import comparisonStore from '@/zustand/comparison';
import { favoritesStore } from '@/zustand/favourite';
import { useGuestStore } from '@/zustand/guestStore';
import useUserStore from '@/zustand/useUser';
import viewedVariantStore from '@/zustand/viewedVariant';
import { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import ContentModal, {
  DraftOrderType,
  GuestDraftOrderType,
} from './components/contentModal';
import { ProductLabels } from './components/productLabels';
import { useLabelsVisibility } from './components/useProductCardLogic';
import ProductBtnGroup from './components/productBtnGroup';
import { useAddToCart } from './components/useAddToCart';

type Props = {
  id?: string;
  slug?: string;
  categorySlug?: string;
  image: string | undefined;
  productType: string | undefined | null;
  title: string | undefined;
  pricePerMonth: number | undefined;
  price?: number | any;
  bordered?: boolean;
  wide?: boolean;
  priceUndiscounted: number | any;
  discount?: number | any;
  like?: boolean;
  availability?: ProductVariantAvailabilityEnum | null | undefined;
  labels?: StickerFragment[];
  bonus?: number | undefined;
  refetch?: () => void;
  className?: any;
  isCompare?: boolean;
  comp?: boolean;
  productId?: string;
  isHover?: boolean;
};

const CategoryCard = ({
  id,
  slug,
  image,
  productType,
  title,
  pricePerMonth,
  price,
  bordered = false,
  priceUndiscounted,
  discount,
  wide,
  availability,
  labels,
  bonus,
  refetch,
  className,
  isCompare = false,
  comp = false,
  isHover = true,
}: Props) => {
  const [openCartModal, setOpenCartModal] = useState(false);
  const { selectedCity } = useCityStore();
  const userData = useUserStore((user: any) => user?.user);
  const isLoggedIn = Boolean(userData?.id);
  const orderId = useGuestStore(state => state.guestOrderId);
  const { data: channelData } = useDefaultChannelQuery();
  const guestComparisonListArray = useGuestStore(state => state.comparisonList);
  const guestFavoriteListArray = useGuestStore(state => state.favoriteList);

  const {
    visibleLabels,
    showAllLabels,
    handleToggleLabels,
    topSectionHeight,
    showToggleButton,
  } = useLabelsVisibility(bonus, discount, labels);

  const { data: draftOrdersData, refetch: refetchDraftOrders } =
    useUserDraftOrdersQuery({
      variables: {
        userId: userData?.id,
      },
    });

  const { data: guestDraftOrdersData } = useDraftOrderQuery({
    variables: {
      id: orderId || '',
    },
    skip: !orderId,
  });
  const languageCode = useLanguageCode();

  const { data: guestComparisonListData, loading: IsGuestComparisonLoading } =
    useProductVariantsQuery({
      variables: {
        ids: (guestComparisonListArray || []).filter(Boolean),
        first: guestComparisonListArray.length,
        city: selectedCity,
        languageCode: languageCode,
      },
    });

  const handleSuccess = (data: AddFavoriteMutation) => {
    if (data?.addFavorite?.errors?.length === 0) {
      setFavorites(data?.addFavorite?.user?.favorites?.edges || []);
    }
  };

  const [create] = useAddFavoriteMutation({
    onCompleted: handleSuccess,
    context: {
      requiresAuth: true,
    },
  });

  const { data: favoriteData, refetch: refetchFavoriteData } =
    useUserFavoritesQuery({
      variables: { first: 10 },
      context: {
        requiresAuth: true,
      },
    });

  useEffect(() => {
    if (IsGuestComparisonLoading) return;
    if (isLoggedIn) return;

    const productComparisonVariants =
      guestComparisonListData?.productVariants?.edges || [];

    const transformedComparisonData = {
      me: {
        comparison: productComparisonVariants.map(edge => edge.node) ?? [],
      },
    };

    setComparison(transformedComparisonData?.me?.comparison);
  }, [!isLoggedIn, !IsGuestComparisonLoading, guestComparisonListArray]);

  const setFavorites = favoritesStore(e => e.setFavorites);

  const isVariantExist = isLoggedIn
    ? draftOrdersData?.userDraftOrders?.[0]?.lines?.some(
        (line: any) => line?.productVariantId === id
      )
    : guestDraftOrdersData?.draftOrder?.lines?.some(
        (line: any) => line?.productVariantId === id
      );

  const setComparison = comparisonStore(e => e.setComparison);
  const comparison = comparisonStore(e => e.comparison);

  const isFavorite = (id: any): boolean => {
    if (isLoggedIn) {
      return Array.isArray(favoriteData?.me?.favorites?.edges)
        ? favoriteData!.me!.favorites!.edges!.some(
            (favorite: any) => favorite?.node?.id === id
          )
        : false;
    } else {
      return Array.isArray(guestFavoriteListArray)
        ? guestFavoriteListArray.includes(id)
        : false;
    }
  };

  const isInComparison = Array.isArray(comparison)
    ? comparison.some((item: any) => item.id === id)
    : false;

  const setViewedVariant = viewedVariantStore(e => e.setViewedVariant);

  const handleCartOpen = () => setOpenCartModal(true);

  const handleCartClose = () => setOpenCartModal(false);

  const handleSuccessVariant = (data: AddViewedVariantMutation) => {
    if (data?.addViewedVariant?.errors?.length === 0) {
      setViewedVariant(data?.addViewedVariant?.user?.viewedVariants?.edges);
      refetch?.();
    }
  };

  const [createVariant] = useAddViewedVariantMutation({
    onCompleted: handleSuccessVariant,
    context: {
      requiresAuth: true,
    },
  });

  const addToVariant = async (id: any) => {
    if (!isLoggedIn) {
      useGuestStore.getState().addToViewedList(id);
      return;
    }

    try {
      await createVariant({
        variables: {
          variant: id,
        },
      });
    } catch (error) {
      console.error('Error on creating variant: ', error);
    }
  };

  const handleFavoriteDelete = (data: RemoveFavoriteMutation) => {
    if (data?.removeFavorite?.errors?.length === 0) {
      setFavorites(data?.removeFavorite?.user?.favorites?.edges || []);
      refetch?.();
    }
  };

  const [remove] = useRemoveFavoriteMutation({
    onCompleted: handleFavoriteDelete,
    context: {
      requiresAuth: true,
    },
  });

  const handleSuccessComparison = (data: AddVariantToComparisonMutation) => {
    if (data?.addVariantToComparison?.errors?.length === 0) {
      setComparison(data?.addVariantToComparison?.user?.comparison);
    }
  };

  const handleDeleteComparison = (
    data: RemoveVariantFromComparisonMutation
  ) => {
    if (data?.removeVariantFromComparison?.errors?.length === 0) {
      setComparison(data.removeVariantFromComparison?.user?.comparison);
    }
  };

  const addToFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await create({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().addToFavorite(id);
      }
    } catch (error) {
      console.error('Error on adding to favorite: ', error);
    }
  };

  const removeFromFavorite = async (id: any) => {
    try {
      if (isLoggedIn) {
        await remove({
          variables: {
            variant: id,
          },
        });
        refetchFavoriteData();
      } else {
        useGuestStore.getState().removeFromFavorite(id);
      }
    } catch (error) {
      console.error('Error on removing from favorite: ', error);
    }
  };

  const addToComparison = async (id: any) => {
    if (!isLoggedIn) {
      useGuestStore.getState().addToComparison(id);
      return;
    }

    try {
      await createComparison({
        variables: {
          variant: id,
        },
      });
    } catch (error) {
      console.error('Error on adding to comparison: ', error);
    }
  };

  const { addToCart } = useAddToCart({
    isLoggedIn,
    availability,
    userData,
    channelData,
    handleCartOpen,
    draftOrdersData,
    refetchDraftOrders,
  });

  const [createComparison] = useAddVariantToComparisonMutation({
    onCompleted: handleSuccessComparison,
    context: {
      requiresAuth: true,
    },
  });

  const [removeComparison] = useRemoveVariantFromComparisonMutation({
    onCompleted: handleDeleteComparison,
    context: {
      requiresAuth: true,
    },
  });

  const removeFromComparison = async (id: any) => {
    if (!isLoggedIn) {
      useGuestStore.getState().removeFromComparison(id);
      return;
    }

    try {
      await removeComparison({
        variables: {
          variant: id,
        },
      });
    } catch (error) {
      console.error('Error on removing from comparison: ', error);
    }
  };

  return (
    <>
      <div
        className={`${styles.cardWrapper} ${className} ${isHover ? styles.cardHover : ''} ${
          wide ? styles.cardWide : ''
        } ${bordered ? styles.bordered : ''}`}
      >
        <ProductLabels
          id={id || ''}
          slug={slug || ''}
          labels={labels || []}
          bonus={bonus || 0}
          discount={discount || 0}
          showAllLabels={showAllLabels}
          topSectionHeight={topSectionHeight}
          handleToggleLabels={handleToggleLabels}
          visibleLabels={visibleLabels}
          showToggleButton={showToggleButton}
          image={image}
          productType={productType || ''}
          title={title || ''}
          pricePerMonth={pricePerMonth}
          availability={availability}
          addToVariant={addToVariant}
        />
        <ProductBtnGroup
          id={id || ''}
          comp={comp}
          price={price}
          discount={discount}
          priceUndiscounted={priceUndiscounted}
          addToCart={addToCart}
          addToFavorite={addToFavorite}
          removeFromFavorite={removeFromFavorite}
          addToComparison={addToComparison}
          removeFromComparison={removeFromComparison}
          isFavorite={isFavorite}
          isInComparison={isInComparison}
          isCompare={isCompare}
          isVariantExist={isVariantExist}
        />
      </div>

      <ContentModal
        openCartModal={openCartModal}
        setOpenCartModal={setOpenCartModal}
        isLoggedIn={isLoggedIn}
        draftOrdersData={draftOrdersData as DraftOrderType}
        guestDraftOrdersData={guestDraftOrdersData as GuestDraftOrderType}
        handleCartClose={handleCartClose}
        id={id}
      />
    </>
  );
};

export default CategoryCard;
